import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ConfigServiceOptions, ConfigService } from '../../origami-api/services/config.service';
import ModuleName = ConfigServiceOptions.ModuleName;
import { NumberHash } from '@app/shared/utils/utils-types';
import { SimpleTopic, Topic } from '../../origami-api/utils/topic-utils';
import { SettingsStore } from './utils/settings-store';
import { MainSettingsModule } from './main';
import { map } from 'rxjs/operators';
import { ErrorCode } from '@app/origami-api/constants';

@Injectable()
export class SettingsService {
  private store: NumberHash<SettingsStore> = {};
  private settings: NumberHash<MainSettingsModule> = {};
  private readonly DOMAIN_ID = 0;
  private realDomainId: number;

  constructor(private configService: ConfigService) {}

  get(siteId: number = this.DOMAIN_ID): MainSettingsModule {
    return this.settings[siteId];
  }

  clear() {
    const domainStore = this.store[this.DOMAIN_ID];

    this.store = {};
    this.settings = {};

    this.store[this.DOMAIN_ID] = domainStore;
    this.settings[this.DOMAIN_ID] = this.createSettingsConfig(this.DOMAIN_ID, domainStore, null);
  }

  loadRoomSettings(roomId: number, modules?: ModuleName[], force?: boolean): Observable<MainSettingsModule> {
    return this.load(roomId, modules, force);
  }

  setDomainId(id: number) {
    this.realDomainId = id;
  }

  loadDomainSettings(modules?: ModuleName[], force?: boolean): Observable<MainSettingsModule> {
    return this.checkAndUpdateStore(this.DOMAIN_ID, modules, force).pipe(
      map((updated) => {
        if (updated) {
          this.settings[this.DOMAIN_ID] = this.createSettingsConfig(this.DOMAIN_ID, this.store[this.DOMAIN_ID], null);

          Object.keys(this.settings).forEach((siteId) => {
            if (+siteId !== this.DOMAIN_ID) {
              this.settings[siteId] = this.createSettingsConfig(
                +siteId,
                this.store[siteId],
                this.store[this.DOMAIN_ID],
              );
            }
          });
        }

        return this.settings[this.DOMAIN_ID];
      }),
    );
  }

  saveRoomSettings(roomId: number, modules?: ModuleName[]): Observable<boolean> {
    return this.settings[roomId].save(modules);
  }

  saveDomainSettings(modules?: ModuleName[]): Observable<boolean> {
    return this.settings[this.DOMAIN_ID].save(modules);
  }

  private load(siteId: number, modules?: ModuleName[], force?: boolean): Observable<MainSettingsModule> {
    return this.checkAndUpdateStore(siteId, modules, force).pipe(
      map((updated) => {
        if (updated) {
          this.settings[siteId] = this.createSettingsConfig(siteId, this.store[siteId], this.store[this.DOMAIN_ID]);
        }
        return this.settings[siteId];
      }),
    );
  }

  private saveTopics(siteId: number, topics: SimpleTopic[]): Observable<boolean> {
    const realSiteId = siteId === this.DOMAIN_ID || siteId === this.realDomainId ? this.realDomainId : siteId;

    return this.configService.setSettings(realSiteId, topics, [ErrorCode.FINISHED_WITH_MINOR_ERRORS]).pipe(
      map((res) => {
        if (res.Success || res.ErrorCode === ErrorCode.FINISHED_WITH_MINOR_ERRORS) {
          topics.forEach((topic) => {
            topic.TopicFields.forEach((field) => {
              if (!this.store[siteId][topic.TopicName]) {
                this.store[siteId][topic.TopicName] = {};
              }

              this.store[siteId][topic.TopicName][field.FieldName] = field;
            });
          });
        }
        return res.Success;
      }),
    );
  }

  private createSettingsConfig(siteId: number, mainStore: SettingsStore, defaultStore: SettingsStore) {
    return new MainSettingsModule(mainStore, defaultStore, (topics) => this.saveTopics(siteId, topics));
  }

  private checkAndUpdateStore(siteId: number, modules?: ModuleName[], isForce?: boolean): Observable<boolean> {
    let force = isForce || !modules || !modules.length;
    if (force && this.store[siteId] && this.store[siteId][ConfigServiceOptions.ModuleName.ViewModeConfiguration]) {
      delete this.store[siteId][ConfigServiceOptions.ModuleName.ViewModeConfiguration];
    }
    modules = (modules || []).filter((moduleName) => !this.store[siteId] || !this.store[siteId][moduleName]);

    if (!force && !modules.length) {
      return of(false);
    }

    return this.configService
      .getSettings(siteId === this.DOMAIN_ID ? undefined : siteId, modules, [], [], isForce ?? false)
      .pipe(
        map((response) => {
          const hash = this.topicsToHash(response.Result || []);
          this.store[siteId] = force ? hash : Object.assign({}, this.store[siteId], hash);
          // modules.forEach(moduleName => this.store[siteId][moduleName] = this.store[siteId][moduleName] || {});
          return true;
        }),
      );
  }

  private topicsToHash(topics: Topic[]): SettingsStore {
    const result = {};

    topics.forEach((topic) => {
      result[topic.TopicName] = {};

      topic.TopicFields.forEach((field) => {
        result[topic.TopicName][field.FieldName] = field;
      });
    });

    return result;
  }
}
